import { useState, useEffect, useRef } from 'react';

const geocode = async (query) => {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'en', 'User-Agent': 'LastDatingApp/1.0' } });
  if (!res.ok) throw new Error('Geocoding failed');
  const data = await res.json();
  return data.map(r => {
    const a = r.address || {};
    const city    = a.city || a.town || a.village || a.municipality || a.county || '';
    const region  = a.state || a.region || a.province || '';
    const country = a.country || '';
    return {
      display: [city, region, country].filter(Boolean).join(', '),
      lat:   parseFloat(r.lat),
      lng:   parseFloat(r.lon),
      tzone: 0,
    };
  }).filter(r => r.display);
};

export default function LocationSearch({ placeholder, onSelect, showCurrentLocation = false, initialValue = '' }) {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [committed, setCommitted] = useState(!!initialValue);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query.trim() || committed) {
      setResults([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await geocode(query);
        setResults(res);
        setOpen(res.length > 0 || showCurrentLocation);
      } catch {
        // silently fail — user can keep typing
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [query, committed, showCurrentLocation]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (e, result) => {
    e.preventDefault();
    e.stopPropagation();
    setQuery(result.display);
    setCommitted(true);
    setOpen(false);
    setResults([]);
    onSelect(result);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setCommitted(false);
  };

  const handleFocus = () => {
    if (!committed && (results.length > 0 || showCurrentLocation)) {
      setOpen(true);
    }
  };

  const handleCurrentLocation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    setOpen(false);
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        try {
          const res = await geocode(`${latitude},${longitude}`);
          if (res[0]) {
            setQuery(res[0].display);
            setCommitted(true);
            setResults([]);
            onSelect(res[0]);
          }
        } catch {
          // ignore
        } finally {
          setGpsLoading(false);
        }
      },
      () => setGpsLoading(false),
      { timeout: 8000 }
    );
  };

  const showDropdown = open && (results.length > 0 || (showCurrentLocation && !committed));

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          className="field"
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        {(loading || gpsLoading) && (
          <div style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)' }}>
            <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1 }} />
          </div>
        )}
      </div>

      {showDropdown && (
        <div className="location-dropdown">
          {showCurrentLocation && !gpsLoading && (
            <button
              type="button"
              className="location-option location-gps-option"
              onMouseDown={handleCurrentLocation}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                <circle cx="12" cy="12" r="9" strokeDasharray="2 3" />
              </svg>
              Use my current location
            </button>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              className="location-option"
              onMouseDown={(e) => handleSelect(e, r)}
            >
              {r.display}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
